import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import classNames from 'classnames';

import { Container } from 'src/components/layouts/Container/Container';
import HeadlineWithButtons from 'src/components/ui/HeadlineWithButtons/HeadlineWithButtons';
import Divider from 'src/components/ui/Divider/Divider';
import Button, { ButtonToneProps } from 'src/components/ui/Button/Button';
import Icon from 'src/components/ui/Icon/Icon';
import { useFileSelect, EXCEL_IMPORT_ACCEPT } from '../hooks/useFileImport';
import { Section, ActionBar } from './Inbound/InboundConfiguratorForm';
import styles from './styles/Inventory.module.scss';
import { AllowedModel, getImportTemplate, uploadImportTemplate } from '../services/utility';

type ContextType = 'inbound' | 'outbound' | 'product_inventory';

interface ImportShellProps {
  selectedFile: File | null;
  FileInput: JSX.Element;
  openFileDialog: () => void;
  onDownloadTemplate: () => void;
  isDownloading: boolean;
  onCancel: () => void;
  onImport: () => void;
  context: ContextType;
}

export const ImportShell: React.FC<ImportShellProps> = ({
  selectedFile,
  FileInput,
  openFileDialog,
  onDownloadTemplate,
  isDownloading,
  onCancel,
  onImport,
  context,
}) => {
  const { t } = useTranslation();

  const headlineKey = `${context}.import_headline`;
  const importInboundsKey = `${context}.import_info`;
  const fileKey = `general.import_file`;
  const selectFileKey = `general.select_file`;
  const noFileSelectedKey = `general.no_file_selected`;
  const helpKey = `general.help`;
  const templateKey = `${context}.template`;
  const downloadingKey = `${context}.downloading`;
  const downloadKey = `general.download`;
  const cancelKey = `general.cancel`;
  const importKey = `general.import`;

  return (
    <Container buttons={[]} className={classNames(styles.root)} title={t(headlineKey)}>
      <HeadlineWithButtons headline={t(headlineKey)} buttons={[]} />
      <div className="flex flex-col gap-y-24">
        <Section className="flex flex-col gap-y-16">
          <div className="typo-text-16--bold">{t(importInboundsKey)}</div>
          <div className="typo-text-16--bold">{t(fileKey)}</div>
          <div className="flex items-center gap-12">
            <Button title={t(selectFileKey)} tone={ButtonToneProps.PRIMARY_BLUE} onClick={openFileDialog} />
            {selectedFile ? <Icon name="file" /> : null}
            <span className="typo-text-14 text-faded">{selectedFile?.name ?? t(noFileSelectedKey)}</span>
          </div>
          {FileInput}
        </Section>

        <Section className="flex flex-col gap-y-16">
          <div className="typo-text-16--bold">{t(helpKey)}</div>
          <div className="grid grid-cols-[min-content,1fr,auto] gap-16 items-center">
            <div className="rounded-base border-subtle p-8">
              <Icon name="file" />
            </div>
            <div className="grid grid-rows-[auto,auto] gap-y-6 col-span-2 md:col-span-1">
              <span className="typo-text-14--bold">{t(templateKey)}</span>
            </div>
            <Button
              disabled={isDownloading}
              title={isDownloading ? t(downloadingKey) : t(downloadKey)}
              className="col-span-full md:col-span-1"
              tone={ButtonToneProps.PRIMARY_BLUE}
              icon="download"
              onClick={onDownloadTemplate}
            />
          </div>
        </Section>
      </div>
      <Divider className="my-24" />

      <ActionBar>
        <Button title={t(cancelKey)} onClick={onCancel} tone={ButtonToneProps.SECONDARY} />
        <Button title={t(importKey)} tone={ButtonToneProps.PRIMARY_BLUE} disabled={!selectedFile} onClick={onImport} />
      </ActionBar>
    </Container>
  );
};

interface ImportDataProps {
  context: ContextType;
}

const contextToModel: Record<ContextType, AllowedModel> = {
  inbound: 'purchase.order',
  outbound: 'sale.order',
  product_inventory: 'product.product',
};

const contextToNavigatePath: Record<ContextType, string> = {
  inbound: '/warehouse/inbound',
  outbound: '/outbound',
  product_inventory: '/warehouse/inventory',
};

const modelToFilename: Record<AllowedModel, string> = {
  'product.product': 'WeShip_Produkt_Vorlage.xlsx',
  'purchase.order': 'WeShip_Wareneingang_Vorlage.xlsx',
  'sale.order': 'WeShip_Warenausgang_Vorlage.xlsx',
};

const ImportData: React.FC<ImportDataProps> = ({ context }) => {
  const navigate = useNavigate();
  const { FileInput, openFileDialog, selectedFile } = useFileSelect(EXCEL_IMPORT_ACCEPT);
  const [isDownloading, setIsDownloading] = useState(false);

  const handleImport = async () => {
    if (!selectedFile) return;
    try {
      const model = contextToModel[context];
      const success = await uploadImportTemplate(model, selectedFile);
      if (success) {
        navigate(contextToNavigatePath[context]);
      } else {
        console.error('Import failed');
      }
    } catch (error) {
      console.error('Import error:', error);
    }
  };

  const onDownloadTemplate = async () => {
    setIsDownloading(true);
    try {
      const model = contextToModel[context];
      const response = await getImportTemplate(model);
      if (!response) {
        console.error('Download failed');
        return;
      }
      const url = window.URL.createObjectURL(response);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', modelToFilename[model] || 'import_template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download failed', err);
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <ImportShell
      selectedFile={selectedFile}
      FileInput={FileInput}
      openFileDialog={openFileDialog}
      onDownloadTemplate={onDownloadTemplate}
      isDownloading={isDownloading}
      onCancel={() => navigate(contextToNavigatePath[context])}
      onImport={handleImport}
      context={context}
    />
  );
};

export default ImportData;
